<template>
  <main class="mobile-page service-screen" :class="{ 'with-action-bar': hasActionBar }">
    <div class="mobile-screen">
      <header class="mobile-topnav">
        <RouterLink class="mobile-icon-btn" to="/mobile/orders" aria-label="返回订单列表">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M14.5 5.5 8 12l6.5 6.5" /></svg>
        </RouterLink>
        <span class="mobile-topnav-title">订单详情</span>
        <i class="nav-space"></i>
      </header>

      <div v-if="loading" class="mobile-card loading-card" aria-live="polite">正在加载订单详情...</div>
      <div v-else-if="message" class="mobile-card message-card" role="alert">
        <p>{{ message }}</p>
        <button class="mobile-btn mobile-btn-primary" type="button" @click="loadOrder">重新加载</button>
      </div>

      <template v-else-if="order">
        <section class="mobile-card status-card">
          <span class="mobile-eyebrow">Order Status</span>
          <h1>{{ statusText(order.orderStatus) }}</h1>
          <p>订单号 · {{ order.orderNo || '-' }}</p>
          <div class="status-meta">
            <span>{{ payText(order) }}</span>
            <span>{{ formatDate(order.createTime) }}</span>
            <span v-if="hasCityPartnerContract(order)">合同 · {{ orderContractStatusText(order) }}</span>
          </div>
        </section>

        <section class="mobile-card detail-card">
          <h2>交易信息</h2>
          <dl>
            <div><dt>订单金额</dt><dd>{{ money(order.totalAmount) }}</dd></div>
            <div><dt>实付金额</dt><dd>{{ money(order.payAmount) }}</dd></div>
            <div><dt>购买数量</dt><dd>{{ order.quantity ?? '-' }}</dd></div>
            <div><dt>支付方式</dt><dd>{{ payMethodText(order.payMethod) }}</dd></div>
          </dl>
        </section>

        <section class="mobile-card detail-card">
          <h2>收货与服务</h2>
          <dl>
            <div><dt>收货人</dt><dd>{{ order.receiverName || '-' }}</dd></div>
            <div><dt>联系电话</dt><dd>{{ order.receiverPhone || '-' }}</dd></div>
            <div class="full"><dt>收货地址</dt><dd>{{ fullAddress(order) }}</dd></div>
            <div class="full" v-if="order.cityPartnerRegionCode">
              <dt>合作区域</dt>
              <dd>{{ regionName(order.cityPartnerRegionCode, order.cityPartnerRegionName) }}</dd>
            </div>
            <div><dt>软件服务联系人</dt><dd>{{ order.serviceContact || '-' }}</dd></div>
            <div><dt>服务联系电话</dt><dd>{{ order.servicePhone || '-' }}</dd></div>
          </dl>
        </section>

        <OrderContractsPanel
          v-if="hasCityPartnerContract(order)"
          variant="mobile"
          :order-id="order.id"
        />

        <section class="mobile-card detail-card">
          <h2>审核与交付</h2>
          <dl>
            <div><dt>订单审核</dt><dd>{{ auditText(order) }}</dd></div>
            <div><dt>银行卡转账</dt><dd>{{ bankText(order.bankTransferStatus) }}</dd></div>
            <div><dt>物流公司</dt><dd>{{ order.logisticsCompany || '-' }}</dd></div>
            <div><dt>物流单号</dt><dd>{{ order.logisticsNo || '-' }}</dd></div>
            <div class="full" v-if="order.orderAuditRemark"><dt>审核备注</dt><dd>{{ order.orderAuditRemark }}</dd></div>
          </dl>
        </section>

        <section v-if="canConfirmReceipt" class="mobile-card receipt-countdown-card" aria-live="polite">
          <span class="mobile-eyebrow">Receipt · 自动签收</span>
          <h2>自动签收倒计时</h2>
          <strong class="receipt-countdown-value">{{ autoConfirmCountdown }}</strong>
          <p>倒计时结束后系统将自动签收。</p>
          <div class="receipt-inline-actions">
            <button class="mobile-btn mobile-btn-primary" type="button" :disabled="confirmingReceipt" @click="confirmReceipt">
              {{ confirmingReceipt ? '正在提交' : '申请签收' }}
            </button>
          </div>
        </section>
      </template>
    </div>

    <div v-if="hasActionBar" class="order-action-bar">
      <div class="action-summary">
        <template v-if="hasPaymentActions">
          <small>订单金额</small>
          <strong>{{ money(order.totalAmount) }}</strong>
        </template>
        <template v-else>
          <small>自动签收倒计时</small>
          <strong class="countdown">{{ autoConfirmCountdown }}</strong>
        </template>
      </div>
      <p v-if="canConfirmReceipt" class="toolbar-hint">请在自动签收倒计时结束前完成操作。</p>
      <div v-if="hasPaymentActions" class="action-buttons">
        <div v-if="canCancelOrder(order)" class="cancel-action">
          <CancelOrderAction :order="order" @canceled="loadOrder" />
        </div>
        <RouterLink v-if="canContinuePay(order)" class="mobile-btn mobile-btn-primary" :to="`/mobile/order?orderId=${order.id}`">
          继续支付
        </RouterLink>
      </div>
      <div v-else-if="canConfirmReceipt" class="action-buttons receipt-actions">
        <button class="mobile-btn mobile-btn-primary" type="button" :disabled="confirmingReceipt" @click="confirmReceipt">
          {{ confirmingReceipt ? '正在提交' : '申请签收' }}
        </button>
      </div>
    </div>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { showToast } from 'vant'
import CancelOrderAction from '@/components/mobile/CancelOrderAction.vue'
import OrderContractsPanel from '@/components/OrderContractsPanel.vue'
import { confirmWebsiteReceipt, getWebsiteOrder } from '@/api/trade'
import { formatChinaAreaAddress, formatChinaRegionName } from '@/data/chinaArea'
import { hasCityPartnerContract, orderContractStatusText } from '@/utils/orderContracts'

const route = useRoute()
const router = useRouter()
const order = ref(null)
const loading = ref(false)
const message = ref('')
const now = ref(Date.now())
const confirmingReceipt = ref(false)
let countdownTimer = null

const receiptDeadline = computed(() => {
  if (!order.value || !order.value.deliveryTime) return 0
  const deliveryTime = parseDateTime(order.value.deliveryTime)
  return deliveryTime ? deliveryTime + 7 * 24 * 60 * 60 * 1000 : 0
})
const autoConfirmDeadline = computed(() => {
  const value = order.value?.autoReceiptDeadline || order.value?.receiptAutoConfirmDeadline || order.value?.receiptAuditDeadline
  return value ? parseDateTime(value) : receiptDeadline.value + 48 * 60 * 60 * 1000
})
const receiptAuditDeadline = computed(() => order.value?.receiptAuditDeadline ? parseDateTime(order.value.receiptAuditDeadline) : 0)
/** 是否处于可申请签收但尚未完成签收的状态。 */
const canConfirmReceipt = computed(() => {
  if (!order.value || Number(order.value.payStatus) !== 1 || Number(order.value.deliveryStatus) !== 1
    || Number(order.value.orderStatus) >= 6 || !receiptDeadline.value) return false
  const auditStatus = Number(order.value.receiptAuditStatus)
  if (auditStatus === 1 || auditStatus === 2) return false
  if (auditStatus === 3 && receiptAuditDeadline.value > now.value) return false
  return now.value >= receiptDeadline.value
})
const hasPaymentActions = computed(() => Boolean(order.value && (canContinuePay(order.value) || canCancelOrder(order.value))))
const hasActionBar = computed(() => hasPaymentActions.value || canConfirmReceipt.value)
const autoConfirmCountdown = computed(() => formatCountdown(autoConfirmDeadline.value - now.value))

async function loadOrder() {
  if (!localStorage.getItem('dzzj_website_token')) {
    router.push({ path: '/mobile/login', query: { redirect: route.fullPath } })
    return
  }
  if (!/^\d+$/.test(String(route.params.id || ''))) {
    message.value = '订单参数无效，请从我的订单中重新打开。'
    return
  }
  loading.value = true
  message.value = ''
  try {
    order.value = await getWebsiteOrder(route.params.id)
  } catch (error) {
    message.value = error.message || '订单详情加载失败'
  } finally {
    loading.value = false
  }
}

function parseDateTime(value) {
  const timestamp = Date.parse(String(value).replace(' ', 'T'))
  return Number.isNaN(timestamp) ? 0 : timestamp
}

function formatCountdown(value) {
  if (Number(value || 0) <= 0) return '0'
  const totalSeconds = Math.max(0, Math.floor(Number(value || 0) / 1000))
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const clock = [hours, minutes, seconds].map(item => String(item).padStart(2, '0')).join(':')
  return days ? `${days}天 ${clock}` : clock
}

async function confirmReceipt() {
  if (!order.value || confirmingReceipt.value) return
  confirmingReceipt.value = true
  try {
    const result = await confirmWebsiteReceipt(order.value.id)
    if (result === false) {
      showToast('订单状态已变化，请刷新后重试')
      await loadOrder()
      return
    }
    showToast('已提交签收审核')
    await loadOrder()
  } catch (error) {
    showToast(error.message || '签收提交失败')
  } finally {
    confirmingReceipt.value = false
  }
}

function statusText(status) {
  return {
    0: '待确认协议',
    1: '待支付',
    2: '待审核',
    3: '审核通过待发货',
    4: '已发货',
    5: '收货审核中',
    6: '已收货',
    7: '已关闭'
  }[Number(status)] || '未知状态'
}

function payText(order) {
  if (Number(order.bankTransferStatus) === 1) return '转账待核对'
  return Number(order.payStatus) === 1 ? '已支付' : '未支付'
}

function canContinuePay(order) {
  return Number(order.payStatus) === 0 && Number(order.orderStatus) === 1 && Number(order.bankTransferStatus) !== 1
}

function canCancelOrder(order) {
  const status = Number(order.orderStatus)
  return Number.isFinite(status) && status >= 0 && status < 3 && Number(order.bankTransferStatus) !== 1
}

function payMethodText(value) {
  if (value === 'ALIPAY') return '支付宝'
  if (value === 'BANK_TRANSFER') return '银行卡转账'
  return value || '-'
}

function auditText(order) {
  if (Number(order.orderStatus) === 2 && Number(order.orderAuditStatus) === 3) return '需要修改'
  return {
    0: '未提交',
    1: '待审核',
    2: '审核通过',
    3: '需要修改',
    4: '直接退款'
  }[Number(order.orderAuditStatus)] || statusText(order.orderStatus)
}

function bankText(status) {
  return {
    1: '待后台核对',
    2: '核对通过',
    3: '核对未通过'
  }[Number(status)] || '未提交'
}

function fullAddress(order) {
  return formatChinaAreaAddress(order) || '-'
}

/**
 * 将合作区域编码转换为完整名称。
 * @param {string} code 县级行政区划编码
 * @param {string} fallbackName 区域名称快照
 * @returns {string} 完整区域名称
 */
function regionName(code, fallbackName) {
  return formatChinaRegionName(code, fallbackName) || '-'
}

function formatDate(value) {
  return value ? String(value).replace('T', ' ').slice(0, 16) : '-'
}

function money(value) {
  const amount = Number(value)
  return Number.isFinite(amount) ? `¥${amount.toFixed(2)}` : '-'
}

onMounted(() => {
  loadOrder()
  countdownTimer = window.setInterval(() => {
    now.value = Date.now()
  }, 1000)
})

onBeforeUnmount(() => {
  if (countdownTimer) window.clearInterval(countdownTimer)
})
</script>

<style scoped>
.service-screen { padding-bottom: calc(104px + env(safe-area-inset-bottom)); }
.service-screen.with-action-bar { padding-bottom: calc(154px + env(safe-area-inset-bottom)); }
.nav-space { width: 36px; flex: 0 0 36px; }
.status-card { margin-top: 22px; }
.status-card h1 { margin: 12px 0 7px; font-size: 25px; font-weight: 900; }
.status-card p { color: var(--mobile-muted); font-size: 12px; word-break: break-all; }
.status-meta { display: flex; flex-wrap: wrap; gap: 6px 12px; margin-top: 13px; }
.status-meta span { padding: 5px 9px; border: 1px solid var(--mobile-line); border-radius: 999px; color: var(--mobile-muted); font-size: 11px; font-weight: 700; background: rgba(255,255,255,.035); }
.detail-card h2 { margin-bottom: 4px; font-size: 15px; font-weight: 900; }
.order-contracts-panel { margin-top: 14px; }
.detail-card dl { display: grid; }
.detail-card dl > div { display: flex; gap: 16px; padding: 11px 0; border-bottom: 1px dashed var(--mobile-line); }
.detail-card dl > div:last-child { border-bottom: none; }
.detail-card dl > .full { display: block; }
.detail-card dt { flex: none; width: 104px; color: var(--mobile-quiet); font-size: 11.5px; font-weight: 700; }
.detail-card dd { flex: 1; min-width: 0; color: var(--mobile-ink); font-size: 12.5px; font-weight: 700; line-height: 1.6; word-break: break-all; }
.loading-card, .message-card { margin-top: 22px; text-align: center; }
.loading-card { color: var(--mobile-muted); font-size: 12.5px; }
.message-card { display: grid; gap: 12px; }
.message-card p { color: #ff9c9c; font-size: 12.5px; line-height: 1.6; }
.order-action-bar { position: fixed; right: 0; bottom: 0; left: 0; z-index: 35; display: grid; gap: 10px; max-width: 540px; margin: 0 auto; padding: 12px 18px calc(12px + env(safe-area-inset-bottom)); border-top: 1px solid var(--mobile-line); background: linear-gradient(180deg, rgba(2,5,13,.74), rgba(2,5,13,.98)); backdrop-filter: blur(12px); }
.action-summary { display: flex; align-items: baseline; justify-content: space-between; gap: 14px; }
.action-summary small { color: var(--mobile-quiet); font-size: 11px; font-weight: 800; }
.action-summary strong { color: var(--mobile-cyan); font-size: 21px; font-weight: 900; }
.action-summary strong.countdown { font-variant-numeric: tabular-nums; letter-spacing: .02em; }
.toolbar-hint { color: var(--mobile-quiet); font-size: 10.5px; line-height: 1.5; }
.action-buttons { display: flex; align-items: center; gap: 10px; }
.cancel-action { flex: 1; min-width: 0; }
.cancel-action :deep(.cancel-order-btn) { width: 100%; min-height: 48px; border-radius: 13px; font-size: 13.5px; }
.action-buttons .mobile-btn { flex: 1.25; min-width: 0; }
.receipt-actions .mobile-btn { flex: 1; }
.receipt-countdown-card { border-color: color-mix(in srgb, var(--mobile-gold) 35%, var(--mobile-line)); background: linear-gradient(145deg, var(--mobile-card), color-mix(in srgb, var(--mobile-gold) 8%, var(--mobile-card))); }
.receipt-countdown-card h2 { margin: 8px 0 4px; }
.receipt-countdown-value { display: block; color: var(--mobile-gold); font-size: 28px; letter-spacing: .02em; }
.receipt-countdown-card p { margin: 8px 0 0; color: var(--mobile-muted); font-size: 12px; }
.receipt-inline-actions { display: flex; gap: 9px; margin-top: 14px; }
.receipt-inline-actions .mobile-btn { flex: 1; }
@media (max-width: 350px) { .detail-card dt { width: 88px; } .action-buttons { flex-wrap: wrap; } .cancel-action, .action-buttons .mobile-btn { flex: 1 1 100%; } }
</style>
